// The session's usage, summed from what the GM's watch holds. A rule cannot
// aggregate; this is the client's ceiling and the meter's arithmetic. Pure.
import { MAX_SESSION_BYTES, type Handout, type HandoutKind, type Refused } from './types';
import { sizeLabel } from '../assets/quota';

export interface Usage {
  bytes: number;
  count: number;
  byKind: Record<HandoutKind, { bytes: number; count: number }>;
  byFolder: Record<string, { bytes: number; count: number }>;
  limit: number;
  fraction: number;
}

export function usage(handouts: readonly Handout[], limit = MAX_SESSION_BYTES): Usage {
  const byKind: Usage['byKind'] = {
    image: { bytes: 0, count: 0 },
    pdf: { bytes: 0, count: 0 },
    note: { bytes: 0, count: 0 },
    link: { bytes: 0, count: 0 },
  };
  const byFolder: Usage['byFolder'] = {};
  let bytes = 0;
  for (const h of handouts) {
    bytes += h.size;
    byKind[h.kind].bytes += h.size;
    byKind[h.kind].count += 1;
    const f = byFolder[h.folder] ?? (byFolder[h.folder] = { bytes: 0, count: 0 });
    f.bytes += h.size;
    f.count += 1;
  }
  return { bytes, count: handouts.length, byKind, byFolder, limit, fraction: limit > 0 ? bytes / limit : 0 };
}

/** Would adding `more` bytes pass the ceiling? */
export function wouldExceed(handouts: readonly Handout[], more: number, limit = MAX_SESSION_BYTES): boolean {
  return usage(handouts, limit).bytes + more > limit;
}

/** The session is full: what a batch lists, and the sentence a single add shows. */
export function quotaRefusal(name: string): Refused {
  const limit = sizeLabel(MAX_SESSION_BYTES);
  return {
    name,
    reason: `the session is at its ${limit} of handouts`,
    sentence: `This session is at its ${limit} of handouts. Remove some first.`,
  };
}

// One size vocabulary for the app: the asset library's meter says sizes the same way.
export { formatBytes, sizeLabel } from '../assets/quota';
